/**
 * Aircraft Model
 * Represents a type/model of aircraft that can be tagged on gallery images.
 * This is separate from the Aircraft collection which represents specific aircraft owned by members.
 */

import { Timestamp } from 'firebase/firestore';
import { AircraftType } from './aircraft';

export interface AircraftModel {
  id: string;                      // Firestore document ID
  name: string;                    // Display name (e.g., "DJI Mavic 3 Pro")
  slug: string;                    // URL-friendly slug (e.g., "dji-mavic-3-pro")
  type: AircraftType;              // 'Fixed Wing' | 'Multi-Rotor'
  manufacturer?: string;           // Optional manufacturer name
  imageUrl?: string;               // Optional representative image
  createdBy: string;               // UID of user who created this model
  createdAt: Timestamp;
  updatedAt: Timestamp;
  usageCount: number;              // Denormalized count for popularity sorting
}

/**
 * Generate a URL-friendly slug from a name
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')     // Remove special characters
    .replace(/\s+/g, '-')          // Replace spaces with hyphens
    .replace(/-+/g, '-')           // Replace multiple hyphens with single
    .replace(/^-|-$/g, '');        // Remove leading/trailing hyphens
}

/**
 * Create a new AircraftModel object with default values
 * Note: Firestore doesn't allow undefined values, so we only include defined optional fields
 */
export function createAircraftModel(
  data: Pick<AircraftModel, 'name' | 'type' | 'createdBy'> & Partial<AircraftModel>
): Omit<AircraftModel, 'id'> {
  const now = Timestamp.now();

  const model: Omit<AircraftModel, 'id'> = {
    name: data.name,
    slug: data.slug || generateSlug(data.name),
    type: data.type,
    createdBy: data.createdBy,
    createdAt: data.createdAt || now,
    updatedAt: data.updatedAt || now,
    usageCount: data.usageCount || 0,
  };

  // Only include optional fields if they have values (Firestore doesn't allow undefined)
  if (data.manufacturer) {
    model.manufacturer = data.manufacturer;
  }
  if (data.imageUrl) {
    model.imageUrl = data.imageUrl;
  }

  return model;
}
